/**
 * 路口标注
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button, message, Popconfirm, Tag } from 'antd';
import gcoord from 'gcoord';
import { importDrawingManagerMap } from '@/utils/map';
import { ownRound } from '@/utils/math';
import LoadingWrapper from '@/components/loadingWrapper';
import {
  crossMarkListRequest,
  deleteCrossMarkRequest,
  upsertCrossMarkRequest
} from '@/service/crossMark';
import OperateModal from './components/operateModal';
import commonStyles from '@/styles/pageContent.module.scss';
import styles from './index.module.scss';

const { CheckableTag } = Tag;
const CrossMark = () => {
  const [loading, setLoading] = useState(false); // loading
  const [roadList, setRoadList] = useState([]); // 路线标注数据
  const [selectRoadId, setSelectRoadId] = useState<any>(null); // 选择的路线ID
  const [operateInfo, setOperateInfo] = useState<any>({}); // 操作的相关信息
  const mapRef: any = useRef(null); // 存储地图实例
  const polygonEditorRef: any = useRef([]); // 存储多边形实例（百度地图中直接存储polygon）

  // 地图初始化
  const initMap = async () => {
    try {
      mapRef.current = new window.BMap.Map('crossMarkMap');

      // 比例尺控件（对应 AMap.Scale）
      mapRef.current.addControl(
        new window.BMap.ScaleControl({
          anchor: window.BMAP_ANCHOR_TOP_LEFT
        })
      );

      // 缩放控件（对应 AMap.ToolBar）
      mapRef.current.addControl(
        new window.BMap.NavigationControl({
          anchor: window.BMAP_ANCHOR_TOP_LEFT
        })
      );

      // 地图类型切换控件（对应 AMap.MapType）
      mapRef.current.addControl(
        new window.BMap.MapTypeControl({
          mapTypes: [window.BMAP_NORMAL_MAP, window.BMAP_HYBRID_MAP]
        })
      );

      // 开启滚轮缩放
      mapRef.current.enableScrollWheelZoom();

      // 设置中心点和缩放级别
      const center = new window.BMap.Point(116.368904, 39.913423);
      mapRef.current.centerAndZoom(center, 12);
    } catch (error) {
      message.error('地图初始化失败');
    }
  };

  // 获取路口标注列表
  const getCrossMarkList = async () => {
    try {
      setLoading(true);
      const res = await crossMarkListRequest();
      setRoadList(res.data?.data || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    importDrawingManagerMap(initMap);
    getCrossMarkList();
  }, []);

  // 显示多边形长宽和删除marker
  const getDistance = (
    paths: any,
    polygon: any,
    existTextLabel?: any,
    existFlagLabel?: any,
    existCloseLabel?: any
  ) => {
    const allLng = paths.map((item: any) => item.lng);
    const allLat = paths.map((item: any) => item.lat);
    const length = mapRef.current.getDistance(
      new window.BMap.Point(Math.min(...allLng), Math.min(...allLat)),
      new window.BMap.Point(Math.max(...allLng), Math.min(...allLat))
    );
    const width = mapRef.current.getDistance(
      new window.BMap.Point(Math.min(...allLng), Math.min(...allLat)),
      new window.BMap.Point(Math.min(...allLng), Math.max(...allLat))
    );
    const text = `长:${ownRound(length, 1)};宽:${ownRound(width, 1)}`;
    if (existTextLabel) {
      // 调整多边形时修改label的内容和位置
      existTextLabel.setPosition(paths[0]);
      existTextLabel.setContent(text);
      existFlagLabel.setPosition(paths[1]);
      existCloseLabel.setPosition(paths[2]);
      return { length, width };
    } else {
      const matchIndex = polygonEditorRef.current.findIndex(
        (item: any) => item === polygon
      );
      const textLabel = new window.BMap.Label(text, {
        position: paths[0],
        offset: new window.BMap.Size(-10, -30)
      });
      textLabel.setStyle({
        cursor: 'default',
        border: '1px solid #ccc',
        padding: '2px 6px',
        background: '#fff',
        fontSize: '12px'
      });
      const flagLabel = new window.BMap.Label(
        `<input name="flag${matchIndex}" type="radio" value="in" />进<input name="flag${matchIndex}" type="radio" value="out" />出`,
        {
          position: paths[1],
          offset: new window.BMap.Size(-10, 0)
        }
      );
      flagLabel.setStyle({
        cursor: 'default',
        border: '1px solid #ccc',
        padding: '2px 6px',
        background: '#fff',
        fontSize: '12px'
      });
      const closeLabel = new window.BMap.Label('删除', {
        position: paths[2],
        offset: new window.BMap.Size(0, 0)
      });
      closeLabel.setStyle({
        cursor: 'pointer',
        border: '1px solid #ccc',
        padding: '2px 6px',
        background: '#fff',
        color: 'red',
        fontSize: '12px'
      });
      closeLabel.addEventListener('click', () => {
        // 点击删除时清空当前多边形所有内容
        polygonEditorRef.current.splice(matchIndex, 1, null);
        polygon.disableEditing();
        mapRef.current.removeOverlay(polygon);
        mapRef.current.removeOverlay(textLabel);
        mapRef.current.removeOverlay(flagLabel);
        mapRef.current.removeOverlay(closeLabel);
      });
      mapRef.current.addOverlay(textLabel);
      mapRef.current.addOverlay(flagLabel);
      mapRef.current.addOverlay(closeLabel);
      return { length, width, textLabel, flagLabel, closeLabel };
    }
  };

  // 生成一个多边形编辑器
  const generatePolygon = (paths: any) => {
    const polygon = new window.BMap.Polygon(paths, {
      strokeColor: '#006600',
      fillColor: '#00FF00',
      strokeWeight: 2,
      strokeOpacity: 0.8,
      fillOpacity: 0.35,
      enableEditing: false
    });
    mapRef.current.addOverlay(polygon);
    // 存储多边形实例（替代高德的 PolygonEditor）
    polygonEditorRef.current.push(polygon);
    const { textLabel, flagLabel, closeLabel } = getDistance(
      paths,
      polygon
    );
    // 监听多边形编辑（拖拽顶点或移动多边形时触发）
    polygon.addEventListener('lineupdate', function () {
      const updatedPath = polygon.getPath();
      getDistance(
        updatedPath,
        polygon,
        textLabel,
        flagLabel,
        closeLabel
      );
    });
    // 开启多边形编辑（对应 polygonEditor.open()）
    polygon.enableEditing();
    // 开启多边形拖拽
    polygon.enableMassClear();
  };

  // 多边形框选
  const drawPolygon = () => {
    const styleOptions = {
      strokeColor: '#006600',
      fillColor: '#00FF00',
      strokeWeight: 2,
      strokeOpacity: 0.8,
      fillOpacity: 0.35
    };
    const drawingManager = new window.BMapLib.DrawingManager(mapRef.current, {
      isOpen: true,
      enableDrawingTool: false,
      drawingMode: window.BMAP_DRAWING_POLYGON,
      polygonOptions: styleOptions
    });
    drawingManager.addEventListener('polygoncomplete', function (overlay: any) {
      // 获取绘制的多边形路径
      const drawnPath = overlay.getPath();
      // 移除 DrawingManager 生成的多边形，使用自定义多边形替代
      mapRef.current.removeOverlay(overlay);
      generatePolygon(drawnPath);
      // 关闭绘图工具
      drawingManager.close();
    });
  };

  // 保存标注
  const save = async () => {
    const content: any = [];
    polygonEditorRef.current.forEach((item: any, index: number) => {
      if (item) {
        const paths = item.getPath();
        // 将经纬度转为WGS84坐标系进行存储
        const transformPaths = paths.map((point: any) =>
          gcoord.transform([point.lng, point.lat], gcoord.BD09, gcoord.WGS84)
        );
        const data: any = { paths: transformPaths };
        const radios: any = document.getElementsByName(`flag${index}`);
        for (let i = 0; i < radios.length; i++) {
          if (radios[i]?.checked) {
            data.flag = radios[i].value;
            break;
          }
        }
        content.push(data);
      }
    });
    const res = await upsertCrossMarkRequest({
      content: JSON.stringify(content),
      id: selectRoadId
    });
    if (res.data?.data) {
      message.success('保存成功');
      getCrossMarkList();
    }
  };

  // 清空地图和多边形编辑器
  const clearMap = () => {
    mapRef.current.clearOverlays();
    polygonEditorRef.current.forEach((item: any) => {
      if (item) {
        item.disableEditing();
      }
    });
    polygonEditorRef.current = [];
  };

  // 选择路线标注
  const onRoadChange = (checked: boolean, road: any) => {
    clearMap();
    if (checked) {
      setSelectRoadId(road.id);
      const parseContent = JSON.parse(road.content || '[]');
      parseContent.forEach((item: any, index: number) => {
        // 将经纬度转为百度地图坐标进行渲染
        const transformPaths = (item.paths || []).map((item1: any) => {
          const result = gcoord.transform(item1, gcoord.WGS84, gcoord.BD09);
          return new window.BMap.Point(result[0], result[1]);
        });
        generatePolygon(transformPaths);
        const radios: any = document.getElementsByName(`flag${index}`);
        for (let i = 0; i < radios.length; i++) {
          if (radios[i]?.value === item.flag) {
            radios[i].checked = true;
            break;
          }
        }
      });
    } else {
      setSelectRoadId(null);
    }
  };

  // 删除路线标注
  const deleteRoad = async (id: number) => {
    const res = await deleteCrossMarkRequest(id);
    if (res.data?.data) {
      message.success('删除成功');
      if (id === selectRoadId) {
        setSelectRoadId(null);
        clearMap();
      }
      getCrossMarkList();
    }
  };

  return (
    <LoadingWrapper spinning={loading} autoHeight style={{ height: '100%' }}>
      <div className={styles.crossMark}>
        <div className={styles.leftPart}>
          <div className={styles.buttonBox}>
            <Button
              type="primary"
              onClick={() => setOperateInfo({ type: 'add' })}
            >
              添加路线标注
            </Button>
          </div>
          <div className={styles.roadBox}>
            {roadList.map((item: any) => (
              <div className={styles.roadItem} key={item.id}>
                <CheckableTag
                  key={item.id}
                  checked={selectRoadId === item.id}
                  onChange={(checked: boolean) => onRoadChange(checked, item)}
                >
                  {item.name}
                </CheckableTag>
                <div className={commonStyles.tableOperateColumn}>
                  <Button
                    type="link"
                    onClick={() => setOperateInfo({ type: 'edit', data: item })}
                  >
                    编辑
                  </Button>
                  <Popconfirm
                    title="确定要删除该路线吗？"
                    onConfirm={() => deleteRoad(item.id)}
                  >
                    <Button type="link">删除</Button>
                  </Popconfirm>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.rightPart}>
          <div className={styles.title}>
            <Button onClick={drawPolygon} disabled={!selectRoadId}>
              添加标注
            </Button>
            <Button onClick={save} type="primary" disabled={!selectRoadId}>
              保存标注
            </Button>
          </div>
          <div id="crossMarkMap" style={{ height: '100%' }} />
        </div>
      </div>
      {['add', 'edit'].includes(operateInfo.type) && (
        <OperateModal
          {...operateInfo}
          closeModal={() => setOperateInfo({})}
          reloadData={getCrossMarkList}
        />
      )}
    </LoadingWrapper>
  );
};

export default CrossMark;
